import { useState } from "react";
import { usePlanoContasGerencial, PlanoContasGerencial, PlanoContasGerencialFormData } from "@/features/contabil/hooks/usePlanoContasGerencial";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Ban } from "lucide-react";
import { usePaginacaoLocal } from "@/hooks/usePaginacaoLocal";
import { PaginacaoRodape } from "@/components/PaginacaoRodape";

const TIPOS_CONTA = [
  { value: 0, label: "Sintética" },
  { value: 1, label: "Analítica" },
];

const NATUREZAS = [
  { value: 1, label: "01 - Ativo" },
  { value: 2, label: "02 - Passivo" },
  { value: 3, label: "03 - Patrimônio Líquido" },
  { value: 4, label: "04 - Resultado" },
  { value: 5, label: "05 - Compensação" },
  { value: 9, label: "09 - Outras" },
];

const TIPOS_NOTA = [
  { value: 0, label: "Nenhum" },
  { value: 1, label: "Entrada" },
  { value: 2, label: "Saída" },
];

const emptyForm = (): PlanoContasGerencialFormData => ({
  codigoPlanoConta: "", descricaoConta: "", contaMestreId: null,
  tipoContaId: null, naturezaContaId: null, tipoNotaId: 0,
  referencia: "", observacao: "", inativo: 0, ibCompoeDRE: false,
});

export default function PlanoDeContasGerencial() {
  const { plano, isLoading, createConta, updateConta, deleteConta } = usePlanoContasGerencial();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<PlanoContasGerencial | null>(null);
  const [inativarId, setInativarId] = useState<number | null>(null);
  const [formData, setFormData] = useState<PlanoContasGerencialFormData>(emptyForm());
  const [busca, setBusca] = useState("");

  const handleOpen = (item?: PlanoContasGerencial) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        codigoPlanoConta: item.codigoPlanoConta || "",
        descricaoConta: item.descricaoConta || "",
        contaMestreId: item.contaMestreId ?? null,
        tipoContaId: item.tipoContaId ?? null,
        naturezaContaId: item.naturezaContaId ?? null,
        tipoNotaId: item.tipoNotaId ?? 0,
        referencia: item.referencia || "",
        observacao: item.observacao || "",
        inativo: item.inativo ?? 0,
        ibCompoeDRE: item.ibCompoeDRE ?? false,
      });
    } else {
      setEditingItem(null);
      setFormData(emptyForm());
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editingItem) {
      await updateConta.mutateAsync({ id: editingItem.id, ...formData });
    } else {
      await createConta.mutateAsync(formData);
    }
    setDialogOpen(false);
  };

  const getContaMestre = (id?: number | null) => {
    const mae = plano.find(p => p.id === id);
    return mae ? `${mae.codigoPlanoConta} - ${mae.descricaoConta}` : "-";
  };

  const isAtivo = (item: PlanoContasGerencial) => (item.inativo ?? 0) === 0;

  const labelNatureza = (id?: number | null) => NATUREZAS.find(n => n.value === id)?.label ?? "-";
  const labelTipo = (id?: number | null) => TIPOS_CONTA.find(t => t.value === id)?.label ?? "-";

  const planoFiltrado = busca
    ? plano.filter(p =>
        p.descricaoConta?.toLowerCase().includes(busca.toLowerCase()) ||
        p.codigoPlanoConta?.toLowerCase().includes(busca.toLowerCase()) ||
        (p.referencia ?? "").toLowerCase().includes(busca.toLowerCase()))
    : plano;

  const paginacao = usePaginacaoLocal(planoFiltrado);

  if (isLoading) return <div className="p-6">Carregando...</div>;

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Plano de Contas Gerencial</h1>
          <p className="text-muted-foreground">Gerencie o plano de contas gerencial</p>
        </div>
        <Button onClick={() => handleOpen()}>
          <Plus className="h-4 w-4 mr-2" />Nova Conta
        </Button>
      </div>

      {/* Busca */}
      <div className="max-w-sm">
        <Input
          placeholder="Buscar por código ou descrição..."
          value={busca}
          onChange={e => setBusca(e.target.value)}
        />
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[120px]">Código</TableHead>
              <TableHead>Descrição</TableHead>
              <TableHead>Conta Mãe</TableHead>
              <TableHead>Natureza</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead className="text-center">DRE</TableHead>
              <TableHead className="text-center">Status</TableHead>
              <TableHead className="w-[80px]">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginacao.itensPaginados.map((item) => (
              <TableRow key={item.id}>
                <TableCell className="font-mono font-medium">{item.codigoPlanoConta || "-"}</TableCell>
                <TableCell>{item.descricaoConta || "-"}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{getContaMestre(item.contaMestreId)}</TableCell>
                <TableCell>{labelNatureza(item.naturezaContaId)}</TableCell>
                <TableCell>{labelTipo(item.tipoContaId)}</TableCell>
                <TableCell className="text-center">
                  {item.ibCompoeDRE && <Badge variant="outline">DRE</Badge>}
                </TableCell>
                <TableCell className="text-center">
                  <Badge variant={isAtivo(item) ? "success" : "secondary"}>
                    {isAtivo(item) ? "Ativo" : "Inativo"}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => handleOpen(item)}><Pencil className="h-4 w-4" /></Button>
                    {isAtivo(item) && (
                      <Button variant="ghost" size="icon" onClick={() => setInativarId(item.id)}><Ban className="h-4 w-4" /></Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {paginacao.itensPaginados.length === 0 && (
              <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">{busca ? "Nenhuma conta encontrada para esta busca." : "Nenhuma conta cadastrada"}</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <PaginacaoRodape
        pagina={paginacao.pagina}
        totalPaginas={paginacao.totalPaginas}
        totalItens={paginacao.totalItens}
        primeiroItem={paginacao.primeiroItem}
        ultimoItem={paginacao.ultimoItem}
        tamanhoPagina={paginacao.tamanhoPagina}
        onPaginaChange={paginacao.setPagina}
        onTamanhoPaginaChange={paginacao.setTamanhoPagina}
      />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>{editingItem ? "Editar Conta" : "Nova Conta"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Código *</Label>
                <Input
                  value={formData.codigoPlanoConta}
                  onChange={(e) => setFormData({ ...formData, codigoPlanoConta: e.target.value })}
                  placeholder="Ex: 1.001.001"
                  required
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label>Descrição *</Label>
                <Input
                  value={formData.descricaoConta}
                  onChange={(e) => setFormData({ ...formData, descricaoConta: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Conta Mãe</Label>
              <Select
                value={formData.contaMestreId?.toString() ?? ""}
                onValueChange={(v) => setFormData({ ...formData, contaMestreId: v ? parseInt(v) : null })}
              >
                <SelectTrigger><SelectValue placeholder="Sem conta mãe" /></SelectTrigger>
                <SelectContent>
                  {plano.filter(p => p.id !== editingItem?.id).map((p) => (
                    <SelectItem key={p.id} value={p.id.toString()}>
                      {p.codigoPlanoConta} - {p.descricaoConta}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Tipo</Label>
                <Select
                  value={formData.tipoContaId?.toString() ?? ""}
                  onValueChange={(v) => setFormData({ ...formData, tipoContaId: v !== "" ? parseInt(v) : null })}
                >
                  <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
                  <SelectContent>
                    {TIPOS_CONTA.map((t) => <SelectItem key={t.value} value={t.value.toString()}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Natureza</Label>
                <Select
                  value={formData.naturezaContaId?.toString() ?? ""}
                  onValueChange={(v) => setFormData({ ...formData, naturezaContaId: v !== "" ? parseInt(v) : null })}
                >
                  <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
                  <SelectContent>
                    {NATUREZAS.map((n) => <SelectItem key={n.value} value={n.value.toString()}>{n.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Tipo Nota</Label>
                <Select
                  value={formData.tipoNotaId?.toString() ?? "0"}
                  onValueChange={(v) => setFormData({ ...formData, tipoNotaId: parseInt(v) })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TIPOS_NOTA.map((t) => <SelectItem key={t.value} value={t.value.toString()}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Referência</Label>
                <Input value={formData.referencia} onChange={(e) => setFormData({ ...formData, referencia: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Observação</Label>
                <Input value={formData.observacao} onChange={(e) => setFormData({ ...formData, observacao: e.target.value })} />
              </div>
            </div>

            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="ibCompoeDRE"
                  checked={formData.ibCompoeDRE ?? false}
                  onCheckedChange={(v) => setFormData({ ...formData, ibCompoeDRE: !!v })}
                />
                <Label htmlFor="ibCompoeDRE">Compõe DRE</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="inativo"
                  checked={(formData.inativo ?? 0) === 1}
                  onCheckedChange={(v) => setFormData({ ...formData, inativo: v ? 1 : 0 })}
                />
                <Label htmlFor="inativo">Inativo</Label>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit">Salvar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!inativarId} onOpenChange={() => setInativarId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Inativar conta</AlertDialogTitle>
            <AlertDialogDescription>Tem certeza que deseja inativar esta conta gerencial?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => { if (inativarId) { deleteConta.mutate(inativarId); setInativarId(null); } }}>
              Inativar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
